import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';

/**
 * Image types the brand-asset endpoints accept.
 *
 * SVG is accepted, but the server rasterizes it to PNG rather than storing the
 * markup — see `SvgBrandImage`. Two SVG-specific rejections survive that and
 * come back as field errors: live `<text>` (no fonts in the container to shape
 * it) and external file references. The server's message names the remedy, so
 * they are not duplicated here.
 */
export const ACCEPTED_ASSET_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
] as const;

/** Server-enforced encoded-size caps (`BrandImageProcessor`), mirrored for the picker. */
export const MAX_LOGO_BYTES = 512 * 1024;
export const MAX_FAVICON_BYTES = 128 * 1024;

export interface AssetUploadFieldProps {
  label: string;
  hint: string;
  /** Current asset URL — uploaded, or a legacy customer-hosted one. Null when unset. */
  currentUrl: string | null;
  /**
   * Shown (muted, with {@link fallbackNote}) when {@link currentUrl} is null —
   * the value that will be used instead. Used by the notification-logo
   * override, which inherits the portal logo when it has none of its own.
   */
  fallbackUrl?: string | null;
  fallbackNote?: string;
  /**
   * True when {@link currentUrl} is a customer-hosted URL we don't own (the
   * server reports no blob name). Drives the "replace this with an upload" note.
   */
  isLegacyUrl?: boolean;
  /** Encoded-size cap in bytes; must match the server's for this asset kind. */
  maxBytes: number;
  pending: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
}

/**
 * Drop-zone + file picker for one brand asset. Feature-agnostic: it knows
 * nothing about routes or asset kinds, only the cap it should enforce and what
 * to call on select/remove.
 *
 * Client-side type and size checks mirror the server's (`BrandImageProcessor`)
 * so an obviously-wrong file fails instantly instead of after a round trip —
 * but they are a convenience, not the gate. The server re-validates by
 * decoding, and its answer is the one that counts.
 */
export function AssetUploadField({
  label,
  hint,
  currentUrl,
  fallbackUrl = null,
  fallbackNote,
  isLegacyUrl = false,
  maxBytes,
  pending,
  onSelect,
  onRemove,
}: AssetUploadFieldProps) {
  const { t } = useTranslation('common');
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  const previewUrl = currentUrl ?? fallbackUrl;
  const showingFallback = !currentUrl && !!fallbackUrl;

  function validate(file: File): string | null {
    if (!(ACCEPTED_ASSET_TYPES as readonly string[]).includes(file.type)) {
      return t('assetUpload.err.type');
    }
    if (file.size > maxBytes) {
      return t('assetUpload.err.size', { max: Math.round(maxBytes / 1024) });
    }
    return null;
  }

  function accept(file: File | undefined) {
    if (!file) return;
    const problem = validate(file);
    setLocalError(problem);
    if (problem) return;
    setPreviewFailed(false);
    onSelect(file);
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{label}</Typography>

      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          accept(e.dataTransfer.files?.[0]);
        }}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          border: '1px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          borderRadius: '10px',
          bgcolor: dragging ? theme.palette.paperAlt : 'transparent',
          transition: 'background 120ms ease, border-color 120ms ease',
        })}
      >
        {/* Thumbnail on an alternate surface so a transparent logo stays visible. */}
        <Box
          sx={(theme) => ({
            width: 96,
            height: 56,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: '6px',
            border: 1,
            borderColor: 'divider',
            bgcolor: theme.palette.paperAlt,
            overflow: 'hidden',
            // An inherited preview is dimmed so it doesn't read as "set".
            opacity: showingFallback ? 0.55 : 1,
          })}
        >
          {previewUrl && !previewFailed ? (
            <Box
              component="img"
              src={previewUrl}
              alt=""
              onError={() => setPreviewFailed(true)}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
              {previewUrl ? t('assetUpload.previewFailed') : t('assetUpload.none')}
            </Typography>
          )}
        </Box>

        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{hint}</Typography>
          {showingFallback && fallbackNote && (
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontStyle: 'italic' }}>
              {fallbackNote}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={pending ? <CircularProgress size={14} /> : <UploadFileOutlined />}
              disabled={pending}
              onClick={() => inputRef.current?.click()}
            >
              {currentUrl ? t('assetUpload.replace') : t('assetUpload.upload')}
            </Button>
            {currentUrl && (
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<DeleteOutlineOutlined />}
                disabled={pending}
                onClick={() => {
                  setLocalError(null);
                  onRemove();
                }}
              >
                {t('assetUpload.remove')}
              </Button>
            )}
          </Stack>
        </Stack>

        <Box
          component="input"
          id={inputId}
          ref={inputRef}
          type="file"
          accept={ACCEPTED_ASSET_TYPES.join(',')}
          aria-label={label}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            accept(e.target.files?.[0]);
            // Reset so re-picking the SAME file fires change again.
            e.target.value = '';
          }}
          sx={{ display: 'none' }}
        />
      </Box>

      {localError && (
        <Alert severity="error" icon={false} sx={{ mt: 1, fontSize: 12 }}>
          {localError}
        </Alert>
      )}
      {isLegacyUrl && !localError && (
        <Alert severity="info" icon={false} sx={{ mt: 1, fontSize: 12 }}>
          {t('assetUpload.legacyUrlNote')}
        </Alert>
      )}
    </Box>
  );
}
